import { useEffect, useState, type JSX } from 'react';

import { cx } from '@emotion/css';
import { Col } from 'antd';
import { FormikProps, useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  doClearGetAccountWithdrawalFundsBalance,
  doGetAccountWithdrawalFundsBalance,
  withdrawFunds,
} from 'src/actions';
import { FilterBarConstant, DefinitionConstant, TransferConstant } from 'src/constants';
import { GetAccountWithdrawalFundsBalanceResponseDto } from 'src/dtos';
import { useAccountBalance, useCumulativeOrders, useSubmittedAccountSelector } from 'src/hooks';
import {
  MFormCurrencyField,
  MFormCurrencyInput,
  MFormTextField,
  MAlert,
  MModal,
  MTypography,
  RequestErrorMessage,
  MSpinner,
} from 'src/lib';
import { OrderStatusLabel, OfferOrderStatusLabel } from 'src/models';
import { Display, FlexDirection, Image, Spacing } from 'src/styles';
import { getCurrencyFormatter, mapFilterToGetOrderListQueryParamsDto, Maybe } from 'src/utils';
import * as Yup from 'yup';

import * as Styles from '../../NewTransfer.styles';
import { NewTransferConfirmStep } from '../NewTransferConfirmStep/NewTransferConfirmStep';

const formValidationSchema = Yup.object().shape({
  amount: Yup.number()
    .typeError('Amount must be a number')
    .required('Amount is required')
    .positive('Amount must be a positive number')
    .max(
      TransferConstant.MAX_AMOUNT,
      `The maximum transfer amount is ${getCurrencyFormatter().format(TransferConstant.MAX_AMOUNT)}`,
    ),
  hasAvailableFunds: Yup.bool().oneOf([true]),
});

export interface NewWithdrawalFormValues {
  amount: string;
  hasAvailableFunds: boolean;
}

export interface NewWithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBankAccount?: any;
}

const initialFormValues: NewWithdrawalFormValues = {
  amount: '',
  hasAvailableFunds: true,
};

export const NewWithdrawalModal = ({ isOpen, onClose, selectedBankAccount }: NewWithdrawalModalProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState<'withdrawal' | 'confirm'>('withdrawal');
  const form = useFormik<NewWithdrawalFormValues>({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: initialFormValues,
    validationSchema: formValidationSchema,
    onSubmit: () => {},
  });
  const [height, setHeight] = useState<number>(400);

  const withdrawalFundsBalance: Maybe<GetAccountWithdrawalFundsBalanceResponseDto> = useSelector(
    (state: any) => state.accountDetails.withdrawalFunds?.data,
  );
  const iswithdrawalFundsBalanceLoading: boolean = useSelector((state: any) =>
    Boolean(state.accountDetails.withdrawalFunds?.isLoading),
  );
  const iswithdrawalFundsBalanceError: boolean = useSelector((state: any) =>
    Boolean(state.accountDetails.withdrawalFunds?.__failed),
  );
  const withdrawalFundsBalanceError: string | undefined = useSelector(
    (state: any) => state.accountDetails.withdrawalFunds?.message,
  );

  const { account } = useSubmittedAccountSelector();
  const { balance, hasBalanceError, isBalanceLoading, balanceError } = useAccountBalance();
  const { cumulativeOrderList, isCumulativeOrderListLoading } = useCumulativeOrders({
    queryParamsDto: mapFilterToGetOrderListQueryParamsDto({
      timeline: FilterBarConstant.FixedTimeline.ThisYear,
      filterBy: [`${OrderStatusLabel.WORKING}_${FilterBarConstant.StatusCategory.Order}`],
    }),
  });

  const uncancelledOfferOrdersTotal = balance.pendingOfferOrdersTotal + balance.pendingExternalOfferOrdersTotal;

  const hasAnyRequestErrors = hasBalanceError || iswithdrawalFundsBalanceError;

  const findCashAvailableForWithdrawal = () => {
    if (withdrawalFundsBalance) {
      return (
        withdrawalFundsBalance.availableFundsForWithdrawal -
        balance.pendingOfferOrdersTotal +
        balance.pendingExternalOfferOrdersTotal
      );
    }

    return 0;
  };

  const hasAnyUncancelledBuyOrders = () => uncancelledOfferOrdersTotal > 0 || cumulativeOrderList.length > 0;

  const hasAvailableFunds = (amount: string) => {
    if (isNaN(Number(amount))) {
      return false;
    }

    if (findCashAvailableForWithdrawal() < Number(amount)) {
      return false;
    }

    return true;
  };

  const updateHeightOnAmountChange = (hasAvailableFunds: boolean) => {
    if (hasAvailableFunds && !hasAnyRequestErrors) {
      setHeight(450);

      return;
    }

    setHeight(675);
  };

  const renderHasUncancelledOfferOrdersValidationMessage = (
    form: FormikProps<NewWithdrawalFormValues>,
  ): JSX.Element | null => {
    if (!form.values.hasAvailableFunds && uncancelledOfferOrdersTotal > 0) {
      return (
        <MAlert
          type='error'
          description={
            <MTypography variant='body1'>
              You have open Offer Order(s), please click{' '}
              <a
                onClick={() => {
                  navigate(
                    `/main/history?show=${JSON.stringify([
                      FilterBarConstant.Show.OfferOrder,
                    ])}&filterBy=${JSON.stringify([
                      `${OfferOrderStatusLabel.PendingFunds}_${FilterBarConstant.StatusCategory.OfferOrder}`,
                      `${OfferOrderStatusLabel.PendingOfferClose}_${FilterBarConstant.StatusCategory.OfferOrder}`,
                      `${OfferOrderStatusLabel.PendingAction}_${FilterBarConstant.StatusCategory.OfferOrder}`,
                      `${OfferOrderStatusLabel.PendingFirmCancellation}_${FilterBarConstant.StatusCategory.OfferOrder}`,
                    ])}`,
                  );
                }}>
                here{' '}
              </a>
              if you would like to view and/or attempt to cancel any order(s) which might give you sufficient funds to
              proceed with your cash withdrawal.
            </MTypography>
          }
        />
      );
    }

    return null;
  };

  const renderHasUncancelledOrdersValidationMessage = (
    form: FormikProps<NewWithdrawalFormValues>,
  ): JSX.Element | null => {
    if (!form.values.hasAvailableFunds && cumulativeOrderList.length > 0) {
      return (
        <MAlert
          type='error'
          description={
            <MTypography variant='body1'>
              You have open Security Buy Order(s), please click{' '}
              <a
                onClick={() => {
                  navigate(
                    `/main/history?show=${JSON.stringify([FilterBarConstant.Show.BuyOrder])}&filterBy=${JSON.stringify([
                      `${OrderStatusLabel.WORKING}_${FilterBarConstant.StatusCategory.Order}`,
                    ])}`,
                  );
                }}>
                here{' '}
              </a>
              if you would like to view and/or attempt to cancel any order(s) which might give you sufficient funds to
              proceed with your cash withdrawal.
            </MTypography>
          }
        />
      );
    }

    return null;
  };

  const renderHasNoAvailableFundsValidationMessage = (
    form: FormikProps<NewWithdrawalFormValues>,
  ): JSX.Element | null => {
    if (!form.values.hasAvailableFunds && hasAnyUncancelledBuyOrders()) {
      return (
        <>
          {renderHasUncancelledOfferOrdersValidationMessage(form)}
          {renderHasUncancelledOrdersValidationMessage(form)}
        </>
      );
    }

    if (!form.values.hasAvailableFunds) {
      return <MAlert description={'You do not have enough available funds to process this request.'} type='error' />;
    }

    return null;
  };

  const renderWithdrawalStep = () => {
    if (isCumulativeOrderListLoading || isBalanceLoading || iswithdrawalFundsBalanceLoading) {
      return <MSpinner height={`${height}px`} />;
    }

    return (
      <>
        <Col span={24}>
          <MFormCurrencyField
            label='Cash Available'
            value={balance.cashAvailable}
            tooltip={{
              type: 'popover',
              content: DefinitionConstant.CASH_AVAILABLE,
              icon: 'question',
            }}
          />
        </Col>

        <Col span={24}>
          <MFormCurrencyField
            label='Funds Available For Withdrawal'
            value={findCashAvailableForWithdrawal()}
            tooltip={{
              type: 'popover',
              content: DefinitionConstant.AVAILABLE_FUNDS_FOR_WITHDRAWAL,
              icon: 'question',
            }}
          />
        </Col>

        <Col span={24}>
          <MFormTextField
            label={
              <div>
                <div className={Styles.senderReceiverTopLabel}>FROM</div>
                <div>My IPO</div>
              </div>
            }
            value={
              <Col span={24} className={cx(Display.flex, FlexDirection.column)}>
                <div>&nbsp;</div>
                <div>{account.accountNumber}</div>
              </Col>
            }
          />
        </Col>

        <Col span={24}>
          <MFormTextField
            label={
              <Col span={24} className={cx(Display.flex, FlexDirection.column)}>
                <div className={Styles.senderReceiverTopLabel}>TO</div>
                <div>
                  {selectedBankAccount.tradingBlockACHRelationship.bankAccountType}
                  <span
                    className={
                      Styles.nickname
                    }>{` (${selectedBankAccount.tradingBlockACHRelationship.nickName})`}</span>
                </div>
              </Col>
            }
            value={
              <Col span={24} className={cx(Display.flex, FlexDirection.column)}>
                <div>
                  {selectedBankAccount.bankName}
                  <img
                    alt='bankLogo'
                    src={
                      selectedBankAccount?.tradingBlockPlaidInstitution?.logo
                        ? `data:image/jpeg;base64,${selectedBankAccount?.tradingBlockPlaidInstitution?.logo}`
                        : Image.BankPlaceholder
                    }
                    className={Styles.bankLogo}
                  />
                </div>
                <div>{selectedBankAccount.tradingBlockACHRelationship.bankAccount}</div>
              </Col>
            }
          />
        </Col>

        <Col span={24} className={Spacing.my12}>
          <MFormCurrencyInput
            label='Withdrawal Amount'
            value={form.values.amount}
            onChange={value => {
              const hasFunds = hasAvailableFunds(value);
              form.setValues(
                {
                  amount: value,
                  hasAvailableFunds: hasFunds,
                },
                true,
              );
              updateHeightOnAmountChange(hasFunds);
            }}
            error={form.errors.amount}
            testId={'money-transfer-withdrawal'}
          />
        </Col>

        <Col span={24} className={cx(Display.flex, FlexDirection.column)}>
          {renderHasNoAvailableFundsValidationMessage(form)}
          {hasBalanceError && <MAlert type='error' description={<RequestErrorMessage message={balanceError} />} />}
          {iswithdrawalFundsBalanceError && (
            <MAlert type='error' description={<RequestErrorMessage message={withdrawalFundsBalanceError} />} />
          )}
        </Col>
      </>
    );
  };

  const renderConfirmStep = () => {
    return (
      <NewTransferConfirmStep
        type='withdrawal'
        amount={form.values.amount}
        selectedBankAccount={selectedBankAccount}
        accountNumber={account.accountNumber}
      />
    );
  };

  const renderContent = () => {
    if (step === 'withdrawal') {
      return renderWithdrawalStep();
    }

    return renderConfirmStep();
  };

  useEffect(() => {
    if (hasBalanceError || iswithdrawalFundsBalanceError) {
      setHeight(675);

      return;
    }
  }, [hasBalanceError, iswithdrawalFundsBalanceError]);

  useEffect(() => {
    dispatch(doGetAccountWithdrawalFundsBalance({ params: { accountId: account.accountId } }));

    return () => {
      dispatch(doClearGetAccountWithdrawalFundsBalance());
    };
  }, [account.accountId]);

  return (
    <MModal
      customWidth={750}
      customHeight={height}
      visible={isOpen}
      title='New Withdrawal'
      primaryButtonText={step === 'withdrawal' ? 'Continue' : 'Transfer'}
      secondaryButtonText={step === 'withdrawal' ? undefined : 'Back'}
      tertiaryButtonText='Close'
      onPrimaryButtonClick={async () => {
        if (step === 'withdrawal') {
          form.submitForm();
          setHeight(475);
          setStep('confirm');
        }

        if (step === 'confirm') {
          onClose();
          dispatch(
            withdrawFunds(selectedBankAccount.tradingBlockACHRelationship.accountId, {
              DisbursementType: 'PartialBalance',
              RequestedAmount: form.values.amount,
              RelationshipId: selectedBankAccount.tradingBlockACHRelationship.id,
            }),
          );
        }
      }}
      onSecondaryButtonClick={() => {
        setStep('withdrawal');
        setHeight(400);
      }}
      onTertiaryButtonClick={onClose}
      isDisabledPrimaryButton={!form.isValid || hasAnyRequestErrors}
      onClose={onClose}>
      {renderContent()}
    </MModal>
  );
};
